<template>
  <div class="widget-install-verify mt-3 pt-3 border-top">
    <div class="d-flex align-items-center mb-2 flex-wrap">
      <strong class="mr-2">Widget validation</strong>
      <span
        v-if="localWidget.is_install_verified"
        class="badge badge-success"
      >Verified</span>
      <span
        v-else
        class="badge badge-warning"
      >Not verified</span>
      <small
        v-if="localWidget.install_manually_verified"
        class="text-muted ml-2"
      >(manual)</small>
    </div>
    <p class="text-muted small mb-2">
      Enter the live page URL where this widget is installed (custom paths and popup/floating
      widgets are supported). Reviews are only pulled for verified widgets.
      If the widget is not verified, new reviews will not be pulled.
    </p>
    <div class="form-row align-items-start">
      <div class="col">
        <input
          v-model="installUrl"
          type="text"
          inputmode="url"
          autocomplete="url"
          class="form-control"
          placeholder="https://example.com/directory/review-widget"
          :disabled="verifying"
          @keyup.enter="verify(false)"
        >
      </div>
      <div class="col-auto">
        <button
          type="button"
          class="btn btn-primary btn-sm"
          :disabled="verifying || !installUrl"
          @click="verify(false)"
        >
          <b-spinner
            v-if="verifying && !manualVerify"
            small
          />
          <span v-else>Verify widget</span>
        </button>
      </div>
      <div
        v-if="isAdmin"
        class="col-auto"
      >
        <button
          type="button"
          class="btn btn-outline-secondary btn-sm"
          :disabled="verifying || !installUrl"
          title="Support override when page check fails (GTM/SPA/etc.)"
          @click="verify(true)"
        >
          <b-spinner
            v-if="verifying && manualVerify"
            small
          />
          <span v-else>Mark verified</span>
        </button>
      </div>
    </div>
    <div
      v-if="errorMessage"
      class="alert alert-danger py-2 px-3 mt-2 mb-0 small"
    >
      {{ errorMessage }}
    </div>
    <div
      v-else-if="localWidget.is_install_verified && localWidget.install_url"
      class="alert alert-success py-2 px-3 mt-2 mb-0 small"
    >
      Verified on {{ localWidget.install_url }}
    </div>
  </div>
</template>

<script>
import Vue from 'vue'
import { mapGetters } from 'vuex'

export default {
  name: 'WidgetInstallVerify',
  props: {
    widget: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      installUrl: this.widget?.install_url || '',
      verifying: false,
      manualVerify: false,
      errorMessage: this.widget?.install_verification_error || '',
      localWidget: { ...this.widget },
    }
  },
  computed: {
    ...mapGetters('auth', ['user']),
    isAdmin() {
      return (
        this.user?.role === 'Admin' ||
        localStorage.getItem('main_access_role') === 'Admin'
      )
    },
  },
  watch: {
    widget: {
      deep: true,
      handler(next) {
        this.localWidget = { ...next }
        this.installUrl = next?.install_url || this.installUrl
        if (!this.verifying) {
          this.errorMessage = next?.install_verification_error || ''
        }
      },
    },
  },
  methods: {
    verify(manual = false) {
      if (!this.installUrl || this.verifying) {
        return
      }
      this.verifying = true
      this.manualVerify = manual
      this.errorMessage = ''
      const url = String(this.installUrl).trim()
      this.$store
        .dispatch('widgets/verifyInstall', {
          widgetId: this.localWidget.id,
          url,
          manual,
        })
        .then((updated) => {
          this.localWidget = { ...updated }
          this.installUrl = updated.install_url || this.installUrl
          this.errorMessage = updated.install_verification_error || ''
          this.$emit('verified', updated)
          Vue.$toast.open({
            message: updated.is_install_verified
              ? 'Widget install verified'
              : (updated.install_verification_error || 'Verification failed'),
            type: updated.is_install_verified ? 'success' : 'error',
            duration: updated.is_install_verified ? 5000 : 9000,
          })
        })
        .catch((err) => {
          const message =
            err?.response?.data?.message ||
            'Verification failed. Check the URL and try again.'
          this.errorMessage = message
          if (err?.response?.data?.data) {
            this.localWidget = { ...err.response.data.data }
          }
          Vue.$toast.open({
            message,
            type: 'error',
            duration: 9000,
          })
        })
        .finally(() => {
          this.verifying = false
          this.manualVerify = false
        })
    },
  },
}
</script>
